import React, { FunctionComponent } from 'react';
import { useQuery } from 'react-apollo';
import { withRouter, match } from 'react-router-dom';
import { compose } from 'recompose';
import { Grid, Header, Statistic } from 'semantic-ui-react';
import withStyles from 'react-jss';
import { isEmpty, pathOr } from 'ramda';
import { FormattedNumber } from 'react-intl';

import { GET_PROFILE } from 'components/UserMgmt/UserMgmt.queries';
import { useUserMgmt } from 'components/UserMgmt/UserMgmt.context';
import { useDatePicker } from 'components/DatePicker/DatePicker.context';
import { ChartPlaceholder } from 'components/Placeholder';
import { ClassProp } from 'helpers/types';
import Query from 'components/Query';

import styles from './Memberships.styles';
import GET_MEMBERSHIP_TOTALS from './Memberships.query';

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = {
  match: match;
  classes: Styles;
};

type MerchantTotal = {
  membershipCount: number;
  membershipLessThan100DollarCount: number;
  membershipGreaterThan100DollarCount: number;
  membershipRevenue: number;
  cashbackAmount: number;
  campaignFeeAmount: number;
  marketingFeeAmount: number;
};

type MerchantTotals = {
  online: MerchantTotal;
  inStore: MerchantTotal;
  totals: MerchantTotal;
};

type QueryResponse = {
  data: MerchantTotals;
  loading: boolean;
};

type NumberFormatOptions = {
  style: string;
  maximumFractionDigits?: number;
  currency?: string;
  currencyDisplay?: string;
};

const numberOptions = {
  style: 'decimal',
  maximumFractionDigits: 1,
};

const dollarOptions = {
  style: 'currency',
  currency: 'USD',
  currencyDisplay: 'symbol',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
};

type MembershipLevelConfig = {
  [key: string]: {
    smallerMembership: string;
    biggerMembership: string;
  };
};

const membershipLevelConfig: MembershipLevelConfig = {
  [`Sam's Club`]: {
    smallerMembership: '$45',
    biggerMembership: '$100',
  },
  ['Costco']: {
    smallerMembership: '$60',
    biggerMembership: '$120',
  },
};

const renderStats = (
  label: string,
  value: number,
  options: NumberFormatOptions,
  classes: Styles,
) => (
  <Statistic>
    <Statistic.Label className={classes.label}>{label}</Statistic.Label>
    <Statistic.Value className={classes.value}>
      <FormattedNumber value={value} {...options} />
    </Statistic.Value>
  </Statistic>
);

const renderMembershipTotals = (merchantName: string, classes: Styles) => (
  response: QueryResponse,
) => {
  if (response.loading) return <ChartPlaceholder className={classes.loading} />;

  const data = pathOr({}, ['data', 'merchantMembershipTotals'], response);
  const { online, inStore, totals } = data;

  return (
    <Grid className={classes.container}>
      <Grid.Row className={classes.headerBlock}>
        <Header className={classes.header} as="h3">
          Memberships
        </Header>
      </Grid.Row>

      <Grid.Row className={classes.statsRow}>
        {renderStats('Online', online.membershipCount, numberOptions, classes)}
        {renderStats(
          'In Store ',
          inStore.membershipCount,
          numberOptions,
          classes,
        )}
        {renderStats(
          pathOr(
            '$45',
            [merchantName, 'smallerMembership'],
            membershipLevelConfig,
          ),
          totals.membershipLessThan100DollarCount,
          numberOptions,
          classes,
        )}
        {renderStats(
          pathOr(
            '$100',
            [merchantName, 'biggerMembership'],
            membershipLevelConfig,
          ),
          totals.membershipGreaterThan100DollarCount,
          numberOptions,
          classes,
        )}
        {renderStats('Total', totals.membershipCount, numberOptions, classes)}
        {renderStats(
          'Total Revenue',
          totals.membershipRevenue,
          dollarOptions,
          classes,
        )}
        {renderStats(
          'Total CashBack',
          totals.cashbackAmount,
          dollarOptions,
          classes,
        )}
      </Grid.Row>
    </Grid>
  );
};

const Memberships: FunctionComponent<Props> = ({ classes, match }) => {
  const userProfileResponse = useQuery(GET_PROFILE);
  const { startDate, endDate } = useDatePicker();
  const { isExternalUser } = useUserMgmt();
  const merchantList = [`Sam's Club`, 'Costco'];

  const {
    data: { userProfile },
    loading,
  } = userProfileResponse;

  if (loading) return null;

  const { merchantId, merchantName }: any =
    isEmpty(match.params) || isExternalUser ? userProfile : match.params;

  if (!merchantList.includes(merchantName)) return null;

  const params = {
    startDate,
    endDate,
    merchantId,
  };

  return (
    <Query
      enableProgressInfo
      query={GET_MEMBERSHIP_TOTALS}
      render={renderMembershipTotals(merchantName, classes)}
      variables={{ params }}
    />
  );
};

export default compose<Props, any>(
  withRouter,
  withStyles(styles as any),
)(Memberships);
